import React from "react";
import { Link } from "react-router-dom";
import {
  Printer,
  ArrowLeft,
  FileText,
  Truck,
  PackagePlus,
  XCircle,
  CheckCircle2,
  Send,
  Sparkles,
  Hourglass,
  AlertTriangle,
} from "lucide-react";

/**
 * Print-friendly Staff Guide for the Footwork PO / Receive cycle.
 * Two-column A4 layout — pin above the receiving bench.
 *
 * Kept intentionally short — the on-screen version in this doc has
 * the full worked examples. This page is the "cheat sheet".
 */
const Pill = ({ bg, fg, border, children }) => (
  <span
    className="inline-block text-[10px] font-black tracking-wider uppercase rounded px-1.5 py-0.5 border-2"
    style={{ background: bg, color: fg, borderColor: border }}
  >
    {children}
  </span>
);

const StaffGuidePrint = () => {
  return (
    <div className="min-h-screen bg-white text-black print:bg-white">
      {/* Header — hidden when printing */}
      <div className="print:hidden bg-[#001740] text-white p-3 flex items-center gap-3 sticky top-0 z-10">
        <Link
          to="/orders"
          className="nb-btn bg-white text-black px-3 py-1.5 text-xs flex items-center gap-1"
          data-testid="staff-guide-back"
        >
          <ArrowLeft size={12} strokeWidth={2.5} /> Back to Orders
        </Link>
        <div className="flex-1 text-sm">
          Footwork — PO & Receive Staff Guide (A4 print)
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="nb-btn bg-[#FFDA6B] border-black text-black px-3 py-1.5 text-xs flex items-center gap-1"
          data-testid="staff-guide-print-btn"
        >
          <Printer size={12} strokeWidth={2.5} /> Print
        </button>
      </div>

      {/* Print sheet */}
      <div
        className="mx-auto p-6 max-w-[210mm] print:p-6 print:max-w-full"
        data-testid="staff-guide-sheet"
      >
        <header className="border-b-4 border-black pb-3 mb-4">
          <div className="text-[10px] font-black tracking-widest uppercase text-[#607087]">
            Footwork · Podlab warehouse
          </div>
          <h1 className="font-display text-3xl leading-none mt-1">
            Purchase Orders & Receiving — Staff Cheat Sheet
          </h1>
          <div className="text-xs text-[#3D4A5C] mt-1">
            Pin this above the receiving bench · v1 · ask the Warehouse Manager if
            unsure
          </div>
        </header>

        <div className="grid grid-cols-2 gap-5 text-[11px] leading-snug">
          {/* Column 1 */}
          <section>
            <h2 className="font-display text-lg mb-1.5 flex items-center gap-1.5">
              <FileText size={16} strokeWidth={2.5} /> The 5 PO states
            </h2>
            <ul className="flex flex-col gap-1.5">
              <li>
                <Pill bg="#FFF6DF" border="#9A6F00" fg="#9A6F00">Draft</Pill>{" "}
                Being built. Not sent. Delete freely.
              </li>
              <li>
                <Pill bg="#DFEEF7" border="#1E7CA8" fg="#1E7CA8">Sent</Pill>{" "}
                Emailed to supplier. Spend committed.
              </li>
              <li>
                <Pill bg="#E6E1F5" border="#4B2E9B" fg="#4B2E9B">Actioned</Pill>{" "}
                Supplier confirmed. <b>Goods have NOT arrived yet.</b>
              </li>
              <li>
                <Pill bg="#FFF3D4" border="#7A5A00" fg="#3B2A00">Part received</Pill>{" "}
                Some lines ticked off. Stays on Receive.
              </li>
              <li>
                <Pill bg="#DFF6E6" border="#107A3E" fg="#107A3E">Delivered</Pill>{" "}
                Fully received. Off the Receive dashboard.
              </li>
              <li>
                <Pill bg="#E4E7EB" border="#3D4A5C" fg="#3D4A5C">Closed</Pill>{" "}
                Received manually — audit only, no stock impact.
              </li>
              <li>
                <Pill bg="#EEE" border="#666" fg="#333">Cancelled</Pill>{" "}
                Supplier pulled it or duplicate. No stock, no bill.
              </li>
            </ul>

            <h2 className="font-display text-lg mt-4 mb-1.5 flex items-center gap-1.5">
              <Sparkles size={16} strokeWidth={2.5} /> The normal cycle
            </h2>
            <ol className="flex flex-col gap-1 list-decimal pl-4">
              <li>
                <b>Orders Mgr raises</b> the PO on <i>Orders &amp; POs</i> (from a
                Suggested reorder or built manually).
              </li>
              <li>
                <b>Send</b> → email leaves via Resend. Pill = Sent.
              </li>
              <li>
                Supplier confirms → tap <b>PO Actioned</b>. Pill = Actioned. PO now
                appears on the <i>Receive</i> dashboard.
              </li>
              <li>
                Truck arrives → open the PO tile on <i>Receive</i>, tick each line
                against the packing slip, tap <b>Receive</b>.
              </li>
              <li>
                All lines ticked → pill = Delivered. Loop closed.
              </li>
            </ol>

            <h2 className="font-display text-lg mt-4 mb-1.5 flex items-center gap-1.5">
              <Hourglass size={16} strokeWidth={2.5} /> Backorders
            </h2>
            <p>
              If only some lines arrived, untick the missing ones and receive
              what you have. PO auto-flips to <i>Part received</i> and stays on
              Receive until the rest lands.
            </p>
          </section>

          {/* Column 2 */}
          <section>
            <h2 className="font-display text-lg mb-1.5 flex items-center gap-1.5">
              <AlertTriangle size={16} strokeWidth={2.5} /> Golden rules
            </h2>
            <ol className="flex flex-col gap-1 list-decimal pl-4">
              <li>
                Orders Mgr owns <b>Draft → Sent → Actioned</b>. Paperwork states.
              </li>
              <li>
                Receiver owns <b>Part received → Delivered</b>. These create real
                stock movements.
              </li>
              <li>
                <b>Actioned ≠ Delivered.</b> If someone asks "did the media
                arrive?", check <i>Receive</i>, not <i>Orders</i>.
              </li>
              <li>
                <b>Never delete a Sent/Actioned PO.</b> Use <i>Cancel</i> — this
                preserves spend history.
              </li>
              <li>
                When in doubt, ask the Warehouse Manager before touching a PO.
              </li>
            </ol>

            <h2 className="font-display text-lg mt-4 mb-1.5 flex items-center gap-1.5">
              <XCircle size={16} strokeWidth={2.5} /> When to tap <i>Cancel</i>
            </h2>
            <p className="mb-1.5">
              The red <b>Cancel</b> button on any non-draft PO (Orders and
              Receive) opens a dialog with 4 reasons. <b>None of them touch
              stock, cost or reports.</b>
            </p>
            <ul className="flex flex-col gap-1 list-disc pl-4">
              <li>
                <b>Already received manually</b> — goods came in, someone
                booked them via manual Receive. Pill becomes <i>Closed</i>.
              </li>
              <li>
                <b>Supplier cancelled the order</b> — factory shutdown, out of
                stock indefinitely. Pill becomes <i>Cancelled</i>.
              </li>
              <li>
                <b>Duplicate</b> — same order already exists on another PO. Pill
                becomes <i>Cancelled</i>.
              </li>
              <li>
                <b>Other</b> — free-text note required. Pill becomes{" "}
                <i>Closed</i>.
              </li>
            </ul>

            <h2 className="font-display text-lg mt-4 mb-1.5 flex items-center gap-1.5">
              <PackagePlus size={16} strokeWidth={2.5} /> Manual Receive
            </h2>
            <p>
              Goods arrived with no PO in the system? Use the right-hand{" "}
              <i>Pick item</i> flow on <i>Receive</i>: pick the item, enter qty
              /supplier/batch/unit cost, tap <b>Receive</b>. Stock and cost
              tracked identically.
            </p>

            <h2 className="font-display text-lg mt-4 mb-1.5 flex items-center gap-1.5">
              <Truck size={16} strokeWidth={2.5} /> Where to look
            </h2>
            <ul className="flex flex-col gap-0.5 list-disc pl-4">
              <li>
                <b>Orders &amp; POs</b> — every PO in every state.
              </li>
              <li>
                <b>Receive</b> — POs still expecting delivery.
              </li>
              <li>
                <b>Reports → Supplier Spend</b> — the money view.
              </li>
              <li>
                <b>Reports → item drill-down</b> — every IN movement, stamped
                with the PO number.
              </li>
            </ul>
          </section>
        </div>

        <footer className="border-t-2 border-black mt-5 pt-2 text-[9px] text-[#607087] flex justify-between">
          <span>Footwork Warehouse · PO cycle staff guide</span>
          <span>Print at 100% · A4</span>
        </footer>
      </div>
    </div>
  );
};

export default StaffGuidePrint;
